import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Announced on the section whenever the purchase selection changes (plan, pack,
 * subscription vs one-time, variant). The sticky add-to-cart bar renders from it —
 * see assets/sticky-add-to-cart.js.
 */
const SELLING_PLAN_CHANGE_EVENT = 'selling-plan:change';

/**
 * Asks for the current selection to be re-announced. Listeners that upgrade after
 * this component dispatch it, since they would have missed the initial broadcast.
 */
const SELLING_PLAN_REQUEST_EVENT = 'selling-plan:request';

class SellingPlanPicker extends Component {
  #abortController = new AbortController();
  #isSubscription = true;
  #selectedPlanId = null;
  #planData = [];
  #currentVariantId = null;
  #formDataListenerAttached = false;
  #hasPackOptions = false;
  /** @type {AbortController | null} */
  #packNavAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Parse plan data
    const planDataEl = this.refs.planData;
    if (planDataEl) {
      try {
        this.#planData = JSON.parse(planDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse plan data', e);
      }
    }

    // Set initial plan
    if (this.#planData.length > 0) {
      this.#selectedPlanId = this.#planData[0].id;
    }

    // Initialize pack options
    this.#hasPackOptions = !!this.refs.packOptions;
    if (this.#hasPackOptions) {
      this.#initPackSelector();
    }

    this.#readCurrentVariantId();

    // Listen for variant changes
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(SELLING_PLAN_REQUEST_EVENT, this.#broadcastSelection, { signal });

    // Attach formdata listener — if the form isn't ready yet, retry after DOM settles
    this.#attachFormDataListener();

    // Reflect the default purchase option (subscription) in the shared price block
    // and in anything else rendering the selection (the sticky add-to-cart bar).
    this.#syncSelection();
  }

  #readCurrentVariantId() {
    const variantInput = this.#getProductForm()?.querySelector('input[name="id"]');
    if (variantInput) this.#currentVariantId = variantInput.value;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    // Remove formdata listener from form
    const form = this.#getProductForm();
    if (form) form.removeEventListener('formdata', this.#onFormData);
  }

  // Inject selling_plan into FormData at submit time — reliable even when
  // this block renders before buy-buttons in the DOM. The pack list is purely
  // informational (its own price shown on its own card) — it never changes
  // what variant this form actually adds to cart.
  #onFormData = (event) => {
    if (this.#isSubscription && this.#selectedPlanId) {
      event.formData.set('selling_plan', String(this.#selectedPlanId));
    } else {
      event.formData.delete('selling_plan');
    }
  };

  #attachFormDataListener() {
    const form = this.#getProductForm();
    if (form) {
      if (!this.#formDataListenerAttached) {
        form.addEventListener('formdata', this.#onFormData);
        this.#formDataListenerAttached = true;
      }
      this.#syncSellingPlanInput();
      return;
    }
    // Form not in DOM yet (buy-buttons block renders after this block).
    // Watch for it via MutationObserver.
    const section = this.closest('.shopify-section');
    if (!section) return;
    const observer = new MutationObserver(() => {
      const f = this.#getProductForm();
      if (f && !this.#formDataListenerAttached) {
        f.addEventListener('formdata', this.#onFormData);
        this.#formDataListenerAttached = true;
        observer.disconnect();
        // The variant input only exists once the form does.
        this.#readCurrentVariantId();
        this.#syncSellingPlanInput();
      }
    });
    observer.observe(section, { childList: true, subtree: true });
    // Disconnect observer when component is removed
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  #getProductForm() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    return section.querySelector('form[data-type="add-to-cart-form"]');
  }

  #syncSellingPlanInput() {
    this.#updateButtonText();
  }

  #updateButtonText() {
    const form = this.#getProductForm();
    if (!form) return;

    const addToCartBtn = form.querySelector('[ref="addToCartButton"]');
    if (!addToCartBtn) return;

    const textEl = addToCartBtn.querySelector('.add-to-cart-text');
    if (!textEl) return;

    // The pack list is informational only — Add to Cart always reflects
    // this product's own price, whichever pack card is highlighted.
    const price = this.#isSubscription
      ? this.#getSubscriptionPrice()
      : parseInt(this.dataset.variantPrice, 10);

    if (!isNaN(price)) {
      const formatted = this.#formatMoney(price);
      textEl.textContent = 'Add To Cart - ' + formatted;
    }
  }

  #getSubscriptionPrice() {
    const plan = this.#planData.find(p => p.id === this.#selectedPlanId);
    if (!plan) return parseInt(this.dataset.variantPrice, 10);

    const variantId = this.#currentVariantId;
    if (variantId && plan.allocations[variantId] !== undefined) {
      return plan.allocations[variantId];
    }

    // Fallback: calculate from adjustment
    const basePrice = parseInt(this.dataset.variantPrice, 10);
    if (plan.adjustmentType === 'percentage') {
      return Math.round(basePrice * (100 - plan.adjustmentValue) / 100);
    } else if (plan.adjustmentType === 'fixed_amount') {
      return Math.max(0, basePrice - plan.adjustmentValue);
    }
    return basePrice;
  }

  /**
   * Future refills price — from the variant's custom.recurring_price_product
   * reference when set, otherwise the subscription price.
   */
  #getRecurringPrice() {
    const plan = this.#planData.find(p => p.id === this.#selectedPlanId);
    const variantId = this.#currentVariantId;
    if (plan?.recurring && variantId && plan.recurring[variantId] !== undefined) {
      return plan.recurring[variantId];
    }
    return this.#getSubscriptionPrice();
  }

  #formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  // Enter/Space activate the div-based radios, listbox options and the frequency
  // toggle, none of which are natively keyboard operable.
  handleKeyActivate(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    // `on:` handlers are delegated from document, so currentTarget is the document —
    // the bound element is exposed as event.target.
    const element = event.target;
    if (!(element instanceof HTMLElement)) return;
    event.preventDefault();
    element.click();
  }

  handleSubscriptionClick() {
    if (this.#isSubscription) return;
    this.#isSubscription = true;
    this.#updateOptionStates();
    this.#syncSellingPlanInput();
  }

  handleOnetimeClick(event) {
    if (event?.target?.closest('.spp__pack-options')) return;
    if (!this.#isSubscription) return;
    this.#isSubscription = false;
    this.#updateOptionStates();
    this.#syncSellingPlanInput();
  }

  #updateOptionStates() {
    const { subscriptionOption, onetimeOption, subscriptionRadio, onetimeRadio, frequencyContainer, packOptions } = this.refs;

    // Never leave the plan list open behind a collapsed subscription card.
    if (!this.#isSubscription) {
      frequencyContainer?.classList.remove('spp__frequency--expanded');
      this.refs.dropdownSummary?.setAttribute('aria-expanded', 'false');
    }

    if (this.#isSubscription) {
      subscriptionOption?.classList.add('spp__option--active');
      onetimeOption?.classList.remove('spp__option--active');
      subscriptionRadio?.classList.add('spp__radio--checked');
      onetimeRadio?.classList.remove('spp__radio--checked');
      if (frequencyContainer) frequencyContainer.style.display = '';
      if (packOptions) packOptions.style.display = 'none';
    } else {
      subscriptionOption?.classList.remove('spp__option--active');
      onetimeOption?.classList.add('spp__option--active');
      subscriptionRadio?.classList.remove('spp__radio--checked');
      onetimeRadio?.classList.add('spp__radio--checked');
      if (frequencyContainer) frequencyContainer.style.display = 'none';
      if (packOptions) packOptions.style.display = '';
      if (this.#hasPackOptions) this.#updateOnetimeDisplay();
    }

    subscriptionOption?.setAttribute('aria-checked', String(this.#isSubscription));
    onetimeOption?.setAttribute('aria-checked', String(!this.#isSubscription));

    this.#updateSummarySection();
    this.#syncSelection();
  }

  togglePlanList() {
    const { frequencyContainer, dropdownSummary } = this.refs;
    if (!frequencyContainer) return;
    const expanded = frequencyContainer.classList.toggle('spp__frequency--expanded');
    dropdownSummary?.setAttribute('aria-expanded', String(expanded));
  }

  #initPackSelector() {
    const items = this.querySelectorAll('.spp__pack-item');
    if (!items.length) return;

    // The markup already marks the pack item matching this product as
    // selected (falling back to the first item only if this product isn't
    // among the pack options) — trust it instead of forcing index 0, so the
    // default one-time price shown in this box matches the current product's
    // own price.
    const selected = this.querySelector('.spp__pack-item--selected') || items[0];
    for (const el of items) el.classList.toggle('spp__pack-item--selected', el === selected);
  }

  #updateOnetimeDisplay() {
    const { onetimePriceDisplay } = this.refs;
    const selectedItem = this.querySelector('.spp__pack-item--selected');
    if (!selectedItem || !onetimePriceDisplay) return;
    const price = parseInt(selectedItem.dataset.price, 10);
    onetimePriceDisplay.textContent = this.#formatMoney(price);
  }

  // Pack sizes are separate products (blocks/pdp-pack-size-options.liquid), not
  // variants of this one — picking a different one loads that product's own
  // page, the same way the PDP pack-size pills do. Rather than a hard
  // navigation (which would reset every component on the page, closing this
  // One-Time Purchase panel back to the Subscription default), this morphs the
  // new page over `main`, the same technique assets/variant-picker.js uses for
  // combined-listing variants that resolve to a different product.
  handlePackSelect(event) {
    event.stopPropagation();
    const item = event.target.closest('.spp__pack-item');
    if (!item || item.classList.contains('spp__pack-item--selected')) return;

    const url = item.dataset.url;
    if (!url) return;

    // The fetch below takes a couple of seconds — without this the whole page
    // sits visibly frozen until it resolves. Cleared for free once the morph
    // below lands, since the freshly-rendered markup never carries either class.
    item.classList.add('spp__pack-item--loading');
    this.refs.packList?.classList.add('is-loading');

    this.#navigateToPackProduct(url);
  }

  /** @param {string} url */
  async #navigateToPackProduct(url) {
    this.#packNavAbortController?.abort();
    const abortController = new AbortController();
    this.#packNavAbortController = abortController;

    let html;
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(String(response.status));
      html = await response.text();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Failed to fetch pack product', error);
      window.location.href = url;
      return;
    }

    const newDocument = new DOMParser().parseFromString(html, 'text/html');
    const main = document.querySelector('main');
    const newMain = newDocument.querySelector('main');
    if (!main || !newMain) {
      window.location.href = url;
      return;
    }

    morph(main, newMain);
    history.replaceState({}, '', url);
    document.title = newDocument.title;

    // Component#updatedCallback() (triggered by the morph above) refreshes
    // `this.refs` asynchronously — wait for that before touching them.
    queueMicrotask(() => this.#resyncAfterPackNavigate());
  }

  // The freshly morphed markup always renders with Subscription as the active
  // option and this product's own plan IDs/allocations — reload what's
  // per-product and reopen One-Time Purchase, since #isSubscription is still
  // false from before this click (pack items are only reachable from there).
  #resyncAfterPackNavigate() {
    const planDataEl = this.refs.planData;
    if (planDataEl) {
      try {
        this.#planData = JSON.parse(planDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse plan data', e);
      }
    }
    this.#selectedPlanId = this.#planData[0]?.id ?? null;

    this.#hasPackOptions = !!this.refs.packOptions;
    if (this.#hasPackOptions) this.#initPackSelector();

    this.#readCurrentVariantId();
    this.#attachFormDataListener();

    this.#updateOptionStates();
  }

  handlePlanSelect(event) {
    const item = event.target.closest('[data-plan-id]');
    if (!item) return;
    this.#selectPlan(item);
  }

  #selectPlan(item) {
    const planId = parseInt(item.dataset.planId, 10);

    this.#selectedPlanId = planId;

    // Update plan list selection highlight
    const allItems = this.querySelectorAll('.spp__plan-item');
    for (const el of allItems) {
      const isSelected = el.dataset.planId === String(planId);
      el.classList.toggle('spp__plan-item--selected', isSelected);
      el.setAttribute('aria-selected', String(isSelected));
    }

    // Update dropdown summary text with selected plan info
    const { dropdownName, dropdownDesc, frequencyContainer, badge, badgeText } = this.refs;
    if (dropdownName) dropdownName.textContent = item.dataset.planName || '';
    if (dropdownDesc) dropdownDesc.textContent = item.dataset.planDesc || '';

    // Update badge to reflect the selected plan's discount percentage
    if (badge && badgeText) {
      const plan = this.#planData.find(p => p.id === planId);
      let discountPercent = 0;
      if (plan) {
        if (plan.adjustmentType === 'percentage') {
          discountPercent = plan.adjustmentValue;
        } else {
          const subPrice = this.#getSubscriptionPrice();
          const variantPrice = parseInt(this.dataset.variantPrice, 10);
          if (variantPrice > 0 && subPrice < variantPrice) {
            discountPercent = Math.round((1 - subPrice / variantPrice) * 100);
          }
        }
      }
      if (discountPercent > 0) {
        const suffix = badge.dataset.badgeSuffix || '';
        badgeText.textContent = `SAVE ${discountPercent}%${suffix}`;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    }

    // Collapse the plan list
    if (frequencyContainer) frequencyContainer.classList.remove('spp__frequency--expanded');
    this.refs.dropdownSummary?.setAttribute('aria-expanded', 'false');

    // Update prices
    this.#updatePriceDisplays();
    this.#syncSelection();
    this.#syncSellingPlanInput();
  }

  #updatePriceDisplays() {
    const subPrice = this.#getSubscriptionPrice();
    const formatted = this.#formatMoney(subPrice);

    const { subPriceDisplay, comparePriceDisplay } = this.refs;

    // Get the unit label from the data-unit attribute set in Liquid
    const unitText = subPriceDisplay?.dataset?.unit || '/filter';

    if (subPriceDisplay) subPriceDisplay.textContent = formatted + unitText;

    // Hide compare-at price if it is not greater than the subscription price
    if (comparePriceDisplay) {
      const comparePrice = parseInt(this.dataset.comparePrice, 10);
      if (isNaN(comparePrice) || comparePrice <= subPrice) {
        comparePriceDisplay.style.display = 'none';
      } else {
        comparePriceDisplay.style.display = '';
      }
    }

    this.#updateSummarySection();
  }

  #updateSummarySection() {
    const { summarySection, summaryCompare, summarySalePrice, savingsBadge, refillsText } = this.refs;
    if (!summarySection) return;

    if (!this.#isSubscription) {
      summarySection.style.display = 'none';
      return;
    }

    summarySection.style.display = '';

    const subPrice = this.#getSubscriptionPrice();
    const comparePrice = parseInt(this.dataset.comparePrice, 10);
    const variantPrice = parseInt(this.dataset.variantPrice, 10);
    const displayCompare = Math.max(comparePrice || 0, variantPrice);

    if (summarySalePrice) summarySalePrice.textContent = this.#formatMoney(subPrice);

    if (summaryCompare) {
      if (displayCompare > subPrice) {
        summaryCompare.textContent = this.#formatMoney(displayCompare);
        summaryCompare.style.display = '';
      } else {
        summaryCompare.style.display = 'none';
      }
    }

    if (savingsBadge) {
      const savings = displayCompare - subPrice;
      if (savings > 0) {
        const badgeTextEl = savingsBadge.querySelector('.spp__badge-text');
        if (badgeTextEl) badgeTextEl.textContent = this.#formatMoney(savings) + ' OFF';
        savingsBadge.style.display = '';
      } else {
        savingsBadge.style.display = 'none';
      }
    }

    if (refillsText) {
      const plan = this.#planData.find(p => p.id === this.#selectedPlanId);
      const planName = plan ? plan.name : (this.dataset.firstPlanName || '');
      refillsText.textContent = 'Future Filter Refills: ' + this.#formatMoney(this.#getRecurringPrice()) + ' ' + planName.toLowerCase();
    }
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    this.#currentVariantId = variant.id.toString();
    this.dataset.variantPrice = variant.price.toString();
    this.dataset.comparePrice = (variant.compare_at_price || variant.price).toString();

    // Re-sync prices and input
    this.#updatePriceDisplays();
    this.#syncSellingPlanInput();

    // Update one-time price display
    const { onetimePriceDisplay } = this.refs;
    if (onetimePriceDisplay) {
      onetimePriceDisplay.textContent = this.#formatMoney(variant.price);
    }

    // Update compare-at price and visibility
    const { comparePriceDisplay } = this.refs;
    if (comparePriceDisplay) {
      const comparePrice = variant.compare_at_price;
      if (comparePrice == null || comparePrice === variant.price) {
        comparePriceDisplay.style.display = 'none';
      } else {
        comparePriceDisplay.style.display = '';
        comparePriceDisplay.textContent = this.#formatMoney(comparePrice);
      }
    }

    this.#syncSelection();
  };

  // Repaints everything outside this component that renders the current selection.
  #syncSelection() {
    this.#updateMainPriceDisplay();
    this.#broadcastSelection();
  }

  /**
   * What the shopper has currently chosen, priced. The single place that resolves
   * "which price applies" — the subscription allocation or the plain variant price —
   * so the buy box and the sticky bar can never disagree. The one-time pack list is
   * informational only (each card shows its own product's price) and never factors
   * in here — this product's own price is always what's charged and displayed.
   *
   * @returns {{ isSubscription: boolean, planId: number | null, planName: string | null,
   *   purchaseLabel: string | null, price: number, compareAtPrice: number | null }}
   */
  #getSelectionState() {
    const variantPrice = parseInt(this.dataset.variantPrice, 10);
    const variantCompare = parseInt(this.dataset.comparePrice, 10);

    let price = variantPrice;
    let compareAtPrice = variantCompare > variantPrice ? variantCompare : null;

    if (this.#isSubscription) {
      const subPrice = this.#getSubscriptionPrice();
      if (subPrice < variantPrice) {
        price = subPrice;
        compareAtPrice = variantPrice;
      }
    }

    const plan = this.#planData.find((entry) => entry.id === this.#selectedPlanId);

    return {
      isSubscription: this.#isSubscription,
      planId: this.#isSubscription ? this.#selectedPlanId : null,
      planName: this.#isSubscription ? (plan?.name ?? this.dataset.firstPlanName ?? null) : null,
      purchaseLabel: this.#isSubscription ? null : (this.dataset.onetimeLabel ?? null),
      price,
      compareAtPrice: compareAtPrice != null && compareAtPrice > price ? compareAtPrice : null,
    };
  }

  // Formatted alongside the raw amounts so every consumer shows the same string —
  // the theme's money formatting is not available to plain markup consumers.
  #broadcastSelection = () => {
    const state = this.#getSelectionState();
    if (isNaN(state.price)) return;

    this.closest('.shopify-section, dialog')?.dispatchEvent(
      new CustomEvent(SELLING_PLAN_CHANGE_EVENT, {
        detail: {
          ...state,
          priceFormatted: this.#formatMoney(state.price),
          compareAtPriceFormatted: state.compareAtPrice == null ? null : this.#formatMoney(state.compareAtPrice),
        },
      })
    );
  };

  // The shared product-price block (blocks/price.liquid, near the product title) has no
  // concept of selling plans — it only compares a variant's price/compare_at_price. Reflect
  // the currently selected purchase option (subscription discount or plain variant price)
  // into its markup directly rather than re-rendering it.
  #updateMainPriceDisplay() {
    const container = this.closest('.shopify-section, dialog')?.querySelector(
      'product-price [ref="priceContainer"]'
    );
    if (!container) return;

    const regular = container.querySelector('.price__regular');
    const sale = container.querySelector('.price__sale');
    if (!regular || !sale) return;

    const regularPriceEl = regular.querySelector('.price');
    const saleAmountEl = sale.querySelector('.price-item--sale.price');
    const saleCompareEl = sale.querySelector('.price-item--regular.compare-at-price');

    const { price, compareAtPrice } = this.#getSelectionState();
    if (isNaN(price)) return;

    const showCompare = compareAtPrice != null;

    if (regularPriceEl) regularPriceEl.textContent = this.#formatMoney(price);
    if (saleAmountEl) saleAmountEl.textContent = this.#formatMoney(price);
    if (saleCompareEl && compareAtPrice != null) saleCompareEl.textContent = this.#formatMoney(compareAtPrice);

    regular.classList.toggle('price__hidden', showCompare);
    sale.classList.toggle('price__hidden', !showCompare);
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
